import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Modal from "react-modal";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  HiOutlineCalendarDays,
  HiOutlineUserGroup,
  HiOutlineMusicalNote,
  HiOutlineGlobeAlt,
  HiOutlineXMark,
  HiOutlineCheck,
  HiOutlineArrowRight,
} from "react-icons/hi2";

const inputBase =
  "w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-[#5932EA]/30 focus:border-[#5932EA] transition-all";
const labelClass =
  "block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2";

const displayFont = { fontFamily: '"Outfit", "Poppins", sans-serif' } as const;

const productBandDefs = [
  {
    id: "home",
    titleKey: "landingBandHomeTitle",
    bodyKey: "landingBandHomeBody",
    image: "/imgs/landing/hero-dashboard.png",
    altKey: "landingHeroImageAlt",
  },
  {
    id: "events",
    titleKey: "landingBandEventsTitle",
    bodyKey: "landingBandEventsBody",
    image: "/imgs/landing/events.png",
    altKey: "landingBandEventsTitle",
  },
  {
    id: "songs",
    titleKey: "landingBandSongsTitle",
    bodyKey: "landingBandSongsBody",
    image: "/imgs/landing/songs.png",
    altKey: "landingBandSongsTitle",
  },
] as const;

const includedKeys = [
  "landingIncludeEvents",
  "landingIncludeSongs",
  "landingIncludeImporter",
  "landingIncludeMinistries",
  "landingIncludeCalendar",
  "landingIncludePlaylist",
  "landingIncludeRoles",
] as const;

const featureDefs = [
  {
    icon: HiOutlineUserGroup,
    titleKey: "landingFeatureGroups",
    bodyKey: "landingFeatureGroupsDesc",
  },
  {
    icon: HiOutlineCalendarDays,
    titleKey: "landingFeatureEvents",
    bodyKey: "landingFeatureEventsDesc",
  },
  {
    icon: HiOutlineMusicalNote,
    titleKey: "landingFeatureSongs",
    bodyKey: "landingFeatureSongsDesc",
  },
  {
    icon: HiOutlineGlobeAlt,
    titleKey: "landingFeatureCalendar",
    bodyKey: "landingFeatureCalendarDesc",
  },
] as const;

const LandingPage: React.FC = () => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [orgName, setOrgName] = useState("");
  const [address, setAddress] = useState("");
  const [contactInfo, setContactInfo] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [membersCount, setMembersCount] = useState("");
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 16);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const openModal = () => setIsModalOpen(true);

  const closeModal = () => {
    setIsModalOpen(false);
    setEmail("");
    setOrgName("");
    setAddress("");
    setContactInfo("");
    setPhoneNumber("");
    setMembersCount("");
    setError("");
    setSuccessMessage("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      !email ||
      !orgName ||
      !address ||
      !contactInfo ||
      !phoneNumber ||
      !membersCount
    ) {
      setError(t("landingAllFieldsRequired") || "All fields are required");
      return;
    }
    try {
      const response = await fetch("/.netlify/functions/sendEmail", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          orgName,
          address,
          contactInfo,
          phoneNumber,
          membersCount,
        }),
      });
      if (response.ok) {
        setSuccessMessage(
          t("landingThankYouMessage") ||
            "Thank you for your interest! We have received your information.",
        );
        setTimeout(closeModal, 3000);
      } else {
        setError(
          t("landingSubmitFailed") ||
            "Failed to send your information. Please try again.",
        );
      }
    } catch {
      setError(
        t("landingErrorOccurred") || "An error occurred. Please try again.",
      );
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col bg-[#f3eefc] text-gray-900"
      style={displayFont}
    >
      <div
        className="pointer-events-none fixed inset-0 -z-10"
        aria-hidden
        style={{
          background:
            "radial-gradient(ellipse 90% 60% at 50% -10%, rgba(89,50,234,0.22), transparent 55%), radial-gradient(ellipse 50% 40% at 100% 20%, rgba(124,58,237,0.12), transparent), linear-gradient(180deg, #f7f4ff 0%, #ebe4ff 45%, #f7f4ff 100%)",
        }}
      />

      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-white/85 backdrop-blur-md border-b border-gray-200/60 shadow-sm"
            : "bg-transparent"
        }`}
      >
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="flex h-16 sm:h-[4.25rem] items-center justify-between gap-3">
            <Link to="/" className="flex items-center gap-2.5 min-w-0">
              <img
                src="/imgs/logo-128x128.png"
                alt=""
                width={40}
                height={40}
                className="w-10 h-10 rounded-xl shrink-0"
              />
              <span className="text-xl font-bold tracking-tight text-gray-900">
                Praisify
              </span>
            </Link>
            <nav className="flex items-center gap-1 sm:gap-3">
              <a
                href="#product"
                className="hidden sm:inline-flex px-3 py-2 text-sm font-medium text-gray-600 hover:text-[#5932EA] transition-colors"
              >
                {t("landingProduct") || "Product"}
              </a>
              <a
                href="#pricing"
                className="hidden sm:inline-flex px-3 py-2 text-sm font-medium text-gray-600 hover:text-[#5932EA] transition-colors"
              >
                {t("landingPricing") || "Pricing"}
              </a>
              <Link
                to="/login"
                className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-[#5932EA] transition-colors"
              >
                {t("login")}
              </Link>
              <Link
                to="/signup"
                className="ml-1 px-4 py-2.5 text-sm font-semibold bg-[#5932EA] text-white rounded-xl hover:bg-[#4a28c7] transition-colors"
              >
                {t("signUp")}
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero — brand, one headline, one line, CTAs, full-bleed product shot */}
      <section className="relative overflow-hidden">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 pt-10 sm:pt-14 pb-0">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: "easeOut" }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="flex flex-col items-center gap-3 mb-6 sm:mb-8">
              <img
                src="/imgs/logo-192x192.png"
                alt="Praisify"
                width={72}
                height={72}
                className="w-[4.5rem] h-[4.5rem] sm:w-20 sm:h-20 rounded-2xl"
              />
              <p className="text-2xl sm:text-3xl font-extrabold tracking-tight text-gray-900">
                Praisify
              </p>
            </div>
            <h1 className="text-3xl sm:text-5xl lg:text-[3.25rem] font-extrabold tracking-tight text-gray-900 leading-[1.1]">
              {t("landingHeroTitle") ||
                "Your whole worship week, in one place."}
            </h1>
            <p className="mt-4 sm:mt-5 text-base sm:text-lg text-gray-600 max-w-xl mx-auto leading-relaxed">
              {t("landingHeroSubtitle") ||
                "Plan services, setlists, and practice so your team shows up ready — without the group chats and spreadsheets."}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
              <Link
                to="/signup"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl bg-[#5932EA] text-white font-semibold hover:bg-[#4a28c7] transition-colors"
              >
                {t("landingStartPricing") || "Start for $10/mo"}
                <HiOutlineArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center justify-center px-7 py-3.5 rounded-xl bg-white/80 border border-gray-200 text-gray-800 font-semibold hover:border-[#5932EA]/40 hover:bg-white transition-colors"
              >
                {t("login")}
              </Link>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.12, ease: "easeOut" }}
          className="mt-10 sm:mt-14 w-full"
        >
          <img
            src="/imgs/landing/hero-dashboard.png"
            alt={
              t("landingHeroImageAlt") ||
              "Praisify home dashboard with events and practice videos"
            }
            className="w-full h-auto max-h-[min(72vh,720px)] object-cover object-top border-t border-gray-200/80"
          />
        </motion.div>
      </section>

      {/* Product bands */}
      <section id="product" className="py-16 sm:py-24 scroll-mt-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 space-y-20 sm:space-y-28">
          {productBandDefs.map((band, i) => (
            <motion.div
              key={band.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.4 }}
              className={`flex flex-col gap-8 lg:gap-12 lg:items-center ${
                i % 2 === 1 ? "lg:flex-row-reverse" : "lg:flex-row"
              }`}
            >
              <div className="lg:w-[42%] min-w-0">
                <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900">
                  {t(band.titleKey)}
                </h2>
                <p className="mt-3 text-base sm:text-lg text-gray-600 leading-relaxed">
                  {t(band.bodyKey)}
                </p>
              </div>
              <div className="lg:flex-1 min-w-0">
                <img
                  src={band.image}
                  alt={t(band.altKey)}
                  className="w-full h-auto rounded-none sm:rounded-2xl border border-gray-200/80 shadow-[0_24px_60px_-28px_rgba(89,50,234,0.35)]"
                />
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* What you get — list, not card grid */}
      <section
        id="features"
        className="py-16 sm:py-20 border-y border-gray-200/70 bg-white/50 scroll-mt-20"
      >
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 text-center">
            {t("landingFeaturesTitle") || "Built for worship teams"}
          </h2>
          <p className="mt-2 text-center text-gray-600 max-w-lg mx-auto">
            {t("landingFeaturesSubtitle") ||
              "Everything your ministry needs to plan the week together."}
          </p>
          <ul className="mt-10 divide-y divide-gray-200/80">
            {featureDefs.map((row) => (
              <li
                key={row.titleKey}
                className="flex items-start gap-4 py-5 first:pt-0 last:pb-0"
              >
                <div className="w-11 h-11 rounded-xl bg-[#5932EA]/10 text-[#5932EA] flex items-center justify-center shrink-0">
                  <row.icon className="w-5 h-5" strokeWidth={2} />
                </div>
                <div className="min-w-0">
                  <h3 className="font-semibold text-gray-900">
                    {t(row.titleKey)}
                  </h3>
                  <p className="mt-1 text-sm sm:text-base text-gray-600 leading-relaxed">
                    {t(row.bodyKey)}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Pricing — single All Access plan */}
      <section id="pricing" className="py-16 sm:py-24 scroll-mt-20">
        <div className="mx-auto max-w-lg px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.4 }}
            className="text-center"
          >
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900">
              {t("landingPricingTitle") || "One plan. Everything included."}
            </h2>
            <p className="mt-2 text-gray-600">
              {t("landingPricingSubtitle") ||
                "Simple pricing for your whole organization."}
            </p>

            <div className="mt-10 rounded-3xl border border-[#5932EA]/25 bg-white/80 backdrop-blur-sm p-8 sm:p-10 text-left shadow-[0_20px_50px_-30px_rgba(89,50,234,0.45)]">
              <p className="text-sm font-semibold uppercase tracking-wide text-[#5932EA]">
                {t("landingPlanName") || "All Access"}
              </p>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="text-5xl font-extrabold tracking-tight text-gray-900">
                  $10
                </span>
                <span className="text-gray-500 font-medium">
                  / {t("landingPerMonth") || "month"}
                </span>
              </div>
              <p className="mt-2 text-sm text-gray-500">
                {t("landingCancelAnytime") || "Cancel anytime. One price for your church."}
              </p>
              <ul className="mt-8 space-y-3">
                {includedKeys.map((key) => (
                  <li
                    key={key}
                    className="flex items-start gap-2.5 text-sm sm:text-base text-gray-700"
                  >
                    <HiOutlineCheck className="w-5 h-5 text-[#5932EA] shrink-0 mt-0.5" />
                    {t(key)}
                  </li>
                ))}
              </ul>
              <Link
                to="/signup"
                className="mt-8 flex w-full items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-[#5932EA] text-white font-semibold hover:bg-[#4a28c7] transition-colors"
              >
                {t("landingStartPricing") || "Start for $10/mo"}
                <HiOutlineArrowRight className="w-4 h-4" />
              </Link>
              <button
                type="button"
                onClick={openModal}
                className="mt-3 w-full py-2.5 text-sm font-medium text-gray-500 hover:text-[#5932EA] transition-colors"
              >
                {t("landingTalkToUs") || "Talk to us first"}
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="py-16 sm:py-20">
        <div className="mx-auto max-w-2xl px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900">
            {t("landingCtaTitle") || "Ready for a calmer Sunday prep?"}
          </h2>
          <p className="mt-3 text-gray-600">
            {t("landingCtaSubtitle") ||
              "Put events, songs, and your team in one home — for $10 a month."}
          </p>
          <Link
            to="/signup"
            className="mt-8 inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-xl bg-[#5932EA] text-white font-semibold hover:bg-[#4a28c7] transition-colors"
          >
            {t("landingGetStarted") || "Create your organization"}
            <HiOutlineArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <footer className="border-t border-gray-200/80 bg-gray-950 text-gray-400 py-12">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2.5">
              <img
                src="/imgs/logo-96x96.png"
                alt=""
                width={36}
                height={36}
                className="w-9 h-9 rounded-xl shrink-0"
              />
              <span className="font-semibold text-gray-200">Praisify</span>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <a href="#pricing" className="hover:text-white transition-colors">
                {t("landingPricing") || "Pricing"}
              </a>
              <Link to="/login" className="hover:text-white transition-colors">
                {t("login")}
              </Link>
              <Link to="/signup" className="hover:text-white transition-colors">
                {t("signUp")}
              </Link>
            </div>
          </div>
          <p className="mt-8 pt-8 border-t border-gray-800 text-sm text-center sm:text-left">
            &copy; {new Date().getFullYear()} Praisify.{" "}
            {t("landingAllRightsReserved") || "All rights reserved."}
          </p>
        </div>
      </footer>

      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        contentLabel={t("landingTalkToUs") || "Talk to us"}
        className="relative bg-white dark:bg-gray-800 p-6 sm:p-8 rounded-2xl shadow-2xl w-full max-w-md mx-4 sm:mx-auto mt-12 sm:mt-24 max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-700 outline-none"
        overlayClassName="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center overflow-y-auto p-4 z-[100]"
      >
        <button
          onClick={closeModal}
          className="absolute top-4 right-4 p-2 rounded-xl text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
          aria-label="Close"
          type="button"
        >
          <HiOutlineXMark className="w-5 h-5" />
        </button>
        {!successMessage ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <h2 className="text-2xl font-bold mb-2 text-gray-800 pr-10">
              {t("landingTalkToUs") || "Talk to us"}
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              {t("landingTalkToUsHint") ||
                "Tell us about your church and we’ll follow up."}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label htmlFor="orgName" className={labelClass}>
                  {t("landingOrgName") || "Organization Name"}
                </label>
                <input
                  type="text"
                  id="orgName"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  required
                  className={inputBase}
                />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="address" className={labelClass}>
                  {t("landingAddress") || "Address"}
                </label>
                <input
                  type="text"
                  id="address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  required
                  className={inputBase}
                />
              </div>
              <div>
                <label htmlFor="contactInfo" className={labelClass}>
                  {t("landingContactName") || "Contact Name"}
                </label>
                <input
                  type="text"
                  id="contactInfo"
                  value={contactInfo}
                  onChange={(e) => setContactInfo(e.target.value)}
                  required
                  className={inputBase}
                />
              </div>
              <div>
                <label htmlFor="phoneNumber" className={labelClass}>
                  {t("landingPhone") || "Phone Number"}
                </label>
                <input
                  type="tel"
                  id="phoneNumber"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  required
                  className={inputBase}
                />
              </div>
              <div>
                <label htmlFor="membersCount" className={labelClass}>
                  {t("landingMembersCount") || "Members Count"}
                </label>
                <input
                  type="number"
                  id="membersCount"
                  value={membersCount}
                  onChange={(e) => setMembersCount(e.target.value)}
                  required
                  className={inputBase}
                />
              </div>
              <div>
                <label htmlFor="email" className={labelClass}>
                  {t("email")}
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className={inputBase}
                />
              </div>
            </div>
            <button
              type="submit"
              className="w-full mt-4 py-3 bg-[#5932EA] text-white font-semibold rounded-xl hover:bg-[#4a28c7] transition-colors"
            >
              {t("landingSubmit") || "Submit"}
            </button>
            {error && (
              <p className="text-sm text-red-500 text-center">{error}</p>
            )}
          </form>
        ) : (
          <div className="text-center py-4">
            <h2 className="text-2xl font-bold mb-2 text-gray-800">
              {t("landingThankYou") || "Thank You!"}
            </h2>
            <p className="text-gray-600 mb-6">{successMessage}</p>
            <button
              type="button"
              onClick={closeModal}
              className="px-6 py-2.5 bg-[#5932EA] text-white font-medium rounded-xl hover:bg-[#4a28c7] transition-colors"
            >
              {t("close")}
            </button>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default LandingPage;
